import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FiHome } from "react-icons/fi";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaUsers,
  FaChartBar,
  FaCheckCircle,
  FaExclamationTriangle,
  FaBook,
  FaTasks,
  FaQuestionCircle,
  FaUserCheck,
  FaGraduationCap,
  FaPen,
  FaTable, // NEW IMPORT
  FaThLarge, // NEW IMPORT
} from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Types
interface MentorStudentProgress {
  studentId: number;
  studentName: string;
  overallProgress: number;
  chaptersProgress: number;
  homeworkProgress: number;
  quizProgress: number;
  writingTaskProgress: number;
  attendanceProgress: number;
  examCompleted: boolean;
  // Add completion counts
  chaptersCompleted?: number;
  chaptersTotal?: number;
  homeworkCompleted?: number;
  homeworkTotal?: number;
  quizzesCompleted?: number;
  quizzesTotal?: number;
  writingTasksCompleted?: number;
  writingTasksTotal?: number;
  classesAttended?: number;
  classesTotal?: number;
}

interface MentorBatchSummary {
  batchId: number;
  batchName: string;
  topic: string;
  totalStudents: number;
  averageProgress: number;
  studentsAbove80Percent: number;
  studentsBelow50Percent: number;
  students: MentorStudentProgress[];
}

const MentorProgressDashboard: React.FC = () => {
  const [batchSummaries, setBatchSummaries] = useState<MentorBatchSummary[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards"); // NEW STATE

  useEffect(() => {
    fetchBatchesOverview();
  }, []);

  const fetchBatchesOverview = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        `${API_BASE}/api/mentor/my-batches/progress-overview`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch batch progress overview");
      }

      const data = await response.json();

      // Calculate completion counts for each student
      const enhancedData = data.map((batch: MentorBatchSummary) => ({
        ...batch,
        students: batch.students.map((student: MentorStudentProgress) => ({
          ...student,
          // Calculate completion counts based on percentages
          chaptersCompleted: Math.round((student.chaptersProgress / 100) * 14),
          chaptersTotal: 14,
          homeworkCompleted: Math.round((student.homeworkProgress / 100) * 14),
          homeworkTotal: 14,
          quizzesCompleted: Math.round((student.quizProgress / 100) * 14),
          quizzesTotal: 14,
          writingTasksCompleted: Math.round(
            (student.writingTaskProgress / 100) * 14
          ),
          writingTasksTotal: 14,
          classesAttended: Math.round(
            (student.attendanceProgress / 100) *
              getTotalClassesForLevel(batch.topic)
          ),
          classesTotal: getTotalClassesForLevel(batch.topic),
        })),
      }));

      setBatchSummaries(enhancedData);
      if (enhancedData.length > 0) {
        setSelectedBatchId(enhancedData[0].batchId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const getTotalClassesForLevel = (level: string) => {
    if (!level) return 35;
    switch (level.toUpperCase()) {
      case "A1":
      case "A2":
        return 35;
      case "B1":
        return 40;
      default:
        return 35;
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 80) return "bg-emerald-500";
    if (percentage >= 60) return "bg-blue-500";
    if (percentage >= 40) return "bg-amber-500";
    return "bg-rose-500";
  };

  const getProgressTextColor = (percentage: number) => {
    if (percentage >= 80) return "text-emerald-600";
    if (percentage >= 60) return "text-blue-600";
    if (percentage >= 40) return "text-amber-600";
    return "text-rose-600";
  };

  const getCardBorderColor = (percentage: number) => {
    if (percentage >= 80)
      return "border-l-4 border-l-emerald-500 bg-emerald-50";
    if (percentage >= 60) return "border-l-4 border-l-blue-500 bg-blue-50";
    if (percentage >= 40) return "border-l-4 border-l-amber-500 bg-amber-50";
    return "border-l-4 border-l-rose-500 bg-rose-50";
  };

  const getStudentCardColor = (progress: number) => {
    if (progress >= 80)
      return "bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200";
    if (progress >= 60)
      return "bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200";
    if (progress >= 40)
      return "bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200";
    return "bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200";
  };

  const getStudentHeaderColor = (progress: number) => {
    if (progress >= 80) return "bg-emerald-400";
    if (progress >= 60) return "bg-blue-400";
    if (progress >= 40) return "bg-amber-400";
    return "bg-rose-400";
  };

  const getTableRowColor = (progress: number) => {
    if (progress >= 80) return "bg-emerald-50";
    if (progress >= 60) return "bg-blue-50";
    if (progress >= 40) return "bg-amber-50";
    return "bg-rose-50";
  };

  const formatProgress = (value: number) => {
    return Math.round(value * 10) / 10;
  };

  const selectedBatch = batchSummaries.find(
    (b) => b.batchId === selectedBatchId
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading progress dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaExclamationTriangle className="mx-auto h-12 w-12 text-rose-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error Loading Dashboard
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchBatchesOverview}
            className="bg-[#009fe3] text-white px-4 py-2 rounded-md hover:bg-cyan-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaChartBar />}
        title="Course Progress Overview"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/mentor/dashboard" },
          { label: "Progress Overview" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {batchSummaries.length === 0 ? (
            <div className="text-center py-12 p-6">
              <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Batches Found
              </h3>
              <p className="text-gray-600">
                You don't have any batches assigned to you yet.
              </p>
            </div>
          ) : (
            <div className="space-y-0">
              {/* Batch Selection Tabs */}
              <div className="border-b border-gray-200 bg-gradient-to-r from-[#009fe3] to-cyan-500 px-6">
                <nav className="-mb-px flex space-x-8">
                  {batchSummaries.map((batch) => (
                    <button
                      key={batch.batchId}
                      onClick={() => setSelectedBatchId(batch.batchId)}
                      className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        selectedBatchId === batch.batchId
                          ? "border-white text-white"
                          : "border-transparent text-cyan-100 hover:text-white hover:border-cyan-200"
                      }`}
                    >
                      {batch.batchName}
                      <span className="ml-2 text-xs opacity-75">
                        ({batch.totalStudents})
                      </span>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Selected Batch Overview */}
              {selectedBatch && (
                <div className="p-6">
                  {/* Batch Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="text-center bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                      <FaUsers className="mx-auto h-8 w-8 text-cyan-600 mb-2" />
                      <div className="text-2xl font-bold text-cyan-700">
                        {selectedBatch.totalStudents}
                      </div>
                      <div className="text-sm text-cyan-600 font-medium">
                        Total Students
                      </div>
                    </div>

                    <div
                      className={`text-center rounded-lg p-4 border ${
                        selectedBatch.averageProgress >= 80
                          ? "bg-emerald-50 border-emerald-200"
                          : selectedBatch.averageProgress >= 60
                          ? "bg-blue-50 border-blue-200"
                          : selectedBatch.averageProgress >= 40
                          ? "bg-amber-50 border-amber-200"
                          : "bg-rose-50 border-rose-200"
                      }`}
                    >
                      <FaChartBar
                        className={`mx-auto h-8 w-8 mb-2 ${getProgressTextColor(
                          selectedBatch.averageProgress
                        )}`}
                      />
                      <div
                        className={`text-2xl font-bold ${getProgressTextColor(
                          selectedBatch.averageProgress
                        )}`}
                      >
                        {formatProgress(selectedBatch.averageProgress)}%
                      </div>
                      <div
                        className={`text-sm font-medium ${getProgressTextColor(
                          selectedBatch.averageProgress
                        )}`}
                      >
                        Average Progress
                      </div>
                    </div>

                    <div className="text-center bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                      <FaCheckCircle className="mx-auto h-8 w-8 text-emerald-600 mb-2" />
                      <div className="text-2xl font-bold text-emerald-700">
                        {selectedBatch.studentsAbove80Percent}
                      </div>
                      <div className="text-sm text-emerald-600 font-medium">
                        Above 80%
                      </div>
                    </div>

                    <div className="text-center bg-rose-50 rounded-lg p-4 border border-rose-200">
                      <FaExclamationTriangle className="mx-auto h-8 w-8 text-rose-600 mb-2" />
                      <div className="text-2xl font-bold text-rose-700">
                        {selectedBatch.studentsBelow50Percent}
                      </div>
                      <div className="text-sm text-rose-600 font-medium">
                        Below 50%
                      </div>
                    </div>
                  </div>

                  {/* Batch Average Progress Bar */}
                  <div
                    className={`mb-8 p-6 rounded-lg border ${getCardBorderColor(
                      selectedBatch.averageProgress
                    )}`}
                  >
                    <div className="flex items-center mb-4">
                      <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white px-4 py-2 rounded-lg">
                        <h3 className="text-lg font-medium flex items-center">
                          <FaChartBar className="mr-2" />
                          Batch Average Progress
                        </h3>
                      </div>
                      <div className="ml-4 text-sm text-gray-600">
                        Topic: {selectedBatch.topic}
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-6 shadow-inner">
                      <div
                        className={`h-6 rounded-full ${getProgressColor(
                          selectedBatch.averageProgress
                        )} shadow-sm transition-all duration-300`}
                        style={{ width: `${selectedBatch.averageProgress}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 mt-3">
                      <span>0%</span>
                      <span
                        className={`font-semibold ${getProgressTextColor(
                          selectedBatch.averageProgress
                        )}`}
                      >
                        {formatProgress(selectedBatch.averageProgress)}%
                        (Average of all students)
                      </span>
                      <span>100%</span>
                    </div>
                  </div>

                  {/* VIEW MODE TOGGLE - NEW */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white px-4 py-2 rounded-lg">
                        <h3 className="text-lg font-medium flex items-center">
                          <FaUsers className="mr-2" />
                          Individual Student Progress
                        </h3>
                      </div>
                      <div className="ml-4 text-sm text-gray-600">
                        {selectedBatch.students.length} students enrolled
                      </div>
                    </div>

                    {/* View Toggle Buttons */}
                    <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                      <button
                        onClick={() => setViewMode("cards")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                          viewMode === "cards"
                            ? "bg-white text-[#009fe3] shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        <FaThLarge />
                        <span className="text-sm font-medium">Cards</span>
                      </button>
                      <button
                        onClick={() => setViewMode("table")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                          viewMode === "table"
                            ? "bg-white text-[#009fe3] shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        <FaTable />
                        <span className="text-sm font-medium">Table</span>
                      </button>
                    </div>
                  </div>

                  {/* CARDS VIEW */}
                  {viewMode === "cards" && (
                    <div className="space-y-6">
                      {selectedBatch.students.map((student) => (
                        <div
                          key={student.studentId}
                          className={`border rounded-xl p-6 ${getStudentCardColor(
                            student.overallProgress
                          )} shadow-sm hover:shadow-md transition-shadow duration-200`}
                        >
                          {/* Student Header with colored strip */}
                          <div className="relative">
                            <div
                              className={`absolute top-0 left-0 w-full h-1 ${getStudentHeaderColor(
                                student.overallProgress
                              )} rounded-t-lg`}
                            ></div>
                            <div className="flex items-center justify-between mb-6 pt-2">
                              <div>
                                <h4 className="font-semibold text-gray-900 text-lg">
                                  {student.studentName}
                                </h4>
                              </div>
                              <div className="flex items-center space-x-3">
                                {student.examCompleted && (
                                  <span className="bg-emerald-100 text-emerald-800 text-xs font-semibold px-3 py-1 rounded-full flex items-center">
                                    <FaGraduationCap className="mr-1" />
                                    Exam Completed
                                  </span>
                                )}
                                <div
                                  className={`px-4 py-2 rounded-lg bg-white shadow-sm border-2 ${
                                    student.overallProgress >= 80
                                      ? "border-emerald-300"
                                      : student.overallProgress >= 60
                                      ? "border-blue-300"
                                      : student.overallProgress >= 40
                                      ? "border-amber-300"
                                      : "border-rose-300"
                                  }`}
                                >
                                  <span
                                    className={`text-xl font-bold ${getProgressTextColor(
                                      student.overallProgress
                                    )}`}
                                  >
                                    {formatProgress(student.overallProgress)}%
                                  </span>
                                  <div className="text-xs text-gray-500">
                                    Overall
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Overall Progress Bar */}
                          <div className="mb-6">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700">
                                Overall Progress
                              </span>
                              <span
                                className={`text-sm font-semibold ${getProgressTextColor(
                                  student.overallProgress
                                )}`}
                              >
                                {formatProgress(student.overallProgress)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4 shadow-inner">
                              <div
                                className={`h-4 rounded-full ${getProgressColor(
                                  student.overallProgress
                                )} shadow-sm transition-all duration-300`}
                                style={{ width: `${student.overallProgress}%` }}
                              ></div>
                            </div>
                          </div>

                          {/* Component Progress Cards - 6 Components */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                            {/* Chapters */}
                            <div className="bg-sky-50 rounded-lg p-4 border border-sky-200">
                              <div className="flex items-center mb-2">
                                <FaBook className="h-4 w-4 text-sky-600 mr-2" />
                                <span className="text-sky-700 font-medium text-sm">
                                  Chapters
                                </span>
                              </div>
                              <div className="w-full bg-sky-200 rounded-full h-3 mb-2">
                                <div
                                  className="h-3 rounded-full bg-sky-500 transition-all duration-300"
                                  style={{
                                    width: `${student.chaptersProgress}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="text-xs text-sky-600 font-medium">
                                {student.chaptersCompleted || 0}/
                                {student.chaptersTotal || 14}
                              </div>
                              <div className="text-sm font-semibold text-sky-700">
                                {formatProgress(student.chaptersProgress)}%
                              </div>
                            </div>

                            {/* Quizzes */}
                            <div className="bg-violet-50 rounded-lg p-4 border border-violet-200">
                              <div className="flex items-center mb-2">
                                <FaQuestionCircle className="h-4 w-4 text-violet-600 mr-2" />
                                <span className="text-violet-700 font-medium text-sm">
                                  Quizzes
                                </span>
                              </div>
                              <div className="w-full bg-violet-200 rounded-full h-3 mb-2">
                                <div
                                  className="h-3 rounded-full bg-violet-500 transition-all duration-300"
                                  style={{
                                    width: `${student.quizProgress}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="text-xs text-violet-600 font-medium">
                                {student.quizzesCompleted || 0}/
                                {student.quizzesTotal || 14}
                              </div>
                              <div className="text-sm font-semibold text-violet-700">
                                {formatProgress(student.quizProgress)}%
                              </div>
                            </div>

                            {/* Homework */}
                            <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                              <div className="flex items-center mb-2">
                                <FaTasks className="h-4 w-4 text-cyan-600 mr-2" />
                                <span className="text-cyan-700 font-medium text-sm">
                                  Homework
                                </span>
                              </div>
                              <div className="w-full bg-cyan-200 rounded-full h-3 mb-2">
                                <div
                                  className="h-3 rounded-full bg-cyan-500 transition-all duration-300"
                                  style={{
                                    width: `${student.homeworkProgress}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="text-xs text-cyan-600 font-medium">
                                {student.homeworkCompleted || 0}/
                                {student.homeworkTotal || 14}
                              </div>
                              <div className="text-sm font-semibold text-cyan-700">
                                {formatProgress(student.homeworkProgress)}%
                              </div>
                            </div>

                            {/* Writing Tasks */}
                            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                              <div className="flex items-center mb-2">
                                <FaPen className="h-4 w-4 text-purple-600 mr-2" />
                                <span className="text-purple-700 font-medium text-sm">
                                  Writing
                                </span>
                              </div>
                              <div className="w-full bg-purple-200 rounded-full h-3 mb-2">
                                <div
                                  className="h-3 rounded-full bg-purple-500 transition-all duration-300"
                                  style={{
                                    width: `${student.writingTaskProgress}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="text-xs text-purple-600 font-medium">
                                {student.writingTasksCompleted || 0}/
                                {student.writingTasksTotal || 14}
                              </div>
                              <div className="text-sm font-semibold text-purple-700">
                                {formatProgress(student.writingTaskProgress)}%
                              </div>
                            </div>

                            {/* Attendance */}
                            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                              <div className="flex items-center mb-2">
                                <FaUserCheck className="h-4 w-4 text-amber-600 mr-2" />
                                <span className="text-amber-700 font-medium text-sm">
                                  Attendance
                                </span>
                              </div>
                              <div className="w-full bg-amber-200 rounded-full h-3 mb-2">
                                <div
                                  className="h-3 rounded-full bg-amber-500 transition-all duration-300"
                                  style={{
                                    width: `${student.attendanceProgress}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="text-xs text-amber-600 font-medium">
                                {student.classesAttended || 0}/
                                {student.classesTotal || 35}
                              </div>
                              <div className="text-sm font-semibold text-amber-700">
                                {formatProgress(student.attendanceProgress)}%
                              </div>
                            </div>

                            {/* Exam */}
                            <div
                              className={`${
                                student.examCompleted
                                  ? "bg-emerald-50 border-emerald-200"
                                  : "bg-slate-50 border-slate-200"
                              } rounded-lg p-4 border`}
                            >
                              <div className="flex items-center mb-2">
                                <FaGraduationCap
                                  className={`h-4 w-4 mr-2 ${
                                    student.examCompleted
                                      ? "text-emerald-600"
                                      : "text-slate-400"
                                  }`}
                                />
                                <span
                                  className={`font-medium text-sm ${
                                    student.examCompleted
                                      ? "text-emerald-700"
                                      : "text-slate-600"
                                  }`}
                                >
                                  Exam
                                </span>
                              </div>
                              <div
                                className={`w-full rounded-full h-3 mb-2 ${
                                  student.examCompleted
                                    ? "bg-emerald-200"
                                    : "bg-slate-200"
                                }`}
                              >
                                <div
                                  className={`h-3 rounded-full transition-all duration-300 ${
                                    student.examCompleted
                                      ? "bg-emerald-500"
                                      : "bg-slate-400"
                                  }`}
                                  style={{
                                    width: `${
                                      student.examCompleted ? 100 : 0
                                    }%`,
                                  }}
                                ></div>
                              </div>
                              <div
                                className={`text-xs font-medium ${
                                  student.examCompleted
                                    ? "text-emerald-600"
                                    : "text-slate-500"
                                }`}
                              >
                                {student.examCompleted ? 1 : 0}/1
                              </div>
                              <div
                                className={`text-sm font-semibold ${
                                  student.examCompleted
                                    ? "text-emerald-700"
                                    : "text-slate-600"
                                }`}
                              >
                                {student.examCompleted ? "100%" : "0%"}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* TABLE VIEW - NEW */}
                  {viewMode === "table" && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                              Student
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Overall
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center justify-center">
                                <FaBook className="mr-1" />
                                Chapters
                              </div>
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center justify-center">
                                <FaQuestionCircle className="mr-1" />
                                Quizzes
                              </div>
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center justify-center">
                                <FaTasks className="mr-1" />
                                Homework
                              </div>
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center justify-center">
                                <FaPen className="mr-1" />
                                Writing
                              </div>
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center justify-center">
                                <FaUserCheck className="mr-1" />
                                Attendance
                              </div>
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <div className="flex items-center justify-center">
                                <FaGraduationCap className="mr-1" />
                                Exam
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedBatch.students.map((student) => (
                            <tr
                              key={student.studentId}
                              className={`hover:bg-gray-50 ${getTableRowColor(
                                student.overallProgress
                              )}`}
                            >
                              {/* Student Name (Sticky) */}
                              <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white z-10">
                                <div className="text-sm font-medium text-gray-900">
                                  {student.studentName}
                                </div>
                              </td>

                              {/* Overall */}
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <span
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                                    student.overallProgress >= 80
                                      ? "bg-emerald-100 text-emerald-800"
                                      : student.overallProgress >= 60
                                      ? "bg-blue-100 text-blue-800"
                                      : student.overallProgress >= 40
                                      ? "bg-amber-100 text-amber-800"
                                      : "bg-rose-100 text-rose-800"
                                  }`}
                                >
                                  {formatProgress(student.overallProgress)}%
                                </span>
                              </td>

                              {/* Chapters */}
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="text-sm font-semibold text-gray-900">
                                  {student.chaptersCompleted}/
                                  {student.chaptersTotal}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatProgress(student.chaptersProgress)}%
                                </div>
                              </td>

                              {/* Quizzes */}
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="text-sm font-semibold text-gray-900">
                                  {student.quizzesCompleted}/
                                  {student.quizzesTotal}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatProgress(student.quizProgress)}%
                                </div>
                              </td>

                              {/* Homework */}
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="text-sm font-semibold text-gray-900">
                                  {student.homeworkCompleted}/
                                  {student.homeworkTotal}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatProgress(student.homeworkProgress)}%
                                </div>
                              </td>

                              {/* Writing Tasks */}
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="text-sm font-semibold text-gray-900">
                                  {student.writingTasksCompleted}/
                                  {student.writingTasksTotal}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatProgress(student.writingTaskProgress)}%
                                </div>
                              </td>

                              {/* Attendance */}
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="text-sm font-semibold text-gray-900">
                                  {student.classesAttended}/
                                  {student.classesTotal}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatProgress(student.attendanceProgress)}%
                                </div>
                              </td>

                              {/* Exam */}
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                {student.examCompleted ? (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                    <FaCheckCircle className="mr-1" />
                                    Completed
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                                    Pending
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default MentorProgressDashboard;
